import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CardMedia,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  MenuBook as BookIcon,
  Star as StarIcon,
  Home as HomeIcon,
  Label as LabelIcon,
  Description as DescriptionIcon,
  QrCode as QrCodeIcon,
  Pages as PagesIcon,
  Summarize as SummarizeIcon,
  Note as NoteIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

import { livresAPI, tagsAPI, UPLOADS_URL } from '../services/api';
import { getEtatLabel } from '../utils/statusUtils';
import { TagList, getLivreTagsWithColors } from '../utils/tagUtils';
import { StatusChip } from '../utils/statusUtils';
import { useTagMutations } from '../hooks/useTagMutations';
import toast from 'react-hot-toast';

function InfoItem({ icon, label, value, showIfEmpty = false }) {
  // Ne pas afficher si pas de valeur (sauf si showIfEmpty est true)
  if (!value && !showIfEmpty) return null;
  
  // Note: InfoItem ne peut pas utiliser useTranslation directement car c'est un composant fonctionnel
  // Le texte "Non renseigné" sera géré par le parent qui passe la valeur traduite
  const isNotSet = value === "Non renseigné" || value === "Not set" || !value;
  const displayValue = value || "";
  
  return (
    <Box sx={{ mb: 2 }}>
      {/* Première ligne : icône + nom du champ */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        {icon && React.cloneElement(icon, { 
          sx: [
            icon.props?.sx || {},
            { 
              color: isNotSet ? 'grey.400' : 'text.secondary', 
              fontSize: '20px', 
              mr: 1, 
              flexShrink: 0 
            }
          ]
        })}
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '14px',
            fontWeight: 400,
            color: 'text.secondary',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {label}
        </Typography>
      </Box>
      {/* Deuxième ligne : valeur alignée avec l'icône */}
      <Typography 
        variant="body1" 
        sx={{ 
          fontSize: '16px',
          fontWeight: 400,
          color: isNotSet ? 'grey.500' : 'text.primary',
          fontStyle: isNotSet ? 'italic' : 'normal',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          ml: 0,
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
}

function LivreDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, src: '', alt: '' });
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Déterminer la locale pour date-fns
  const dateLocale = i18n.language === 'en' ? enUS : fr;

  const { data: livre, isLoading, error } = useQuery(
    ['livre', id, forceUpdate],
    () => livresAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  // Requête pour récupérer tous les tags disponibles
  const { data: tagsData } = useQuery(
    'tags-list',
    () => tagsAPI.getAll().then(res => res.data.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Hook personnalisé pour les mutations de tags
  const { addTagMutation, removeTagMutation, isTagLoading } = useTagMutations(livre, tagsData, null, setForceUpdate);

  const deleteLivreMutation = useMutation(
    () => livresAPI.delete(id),
    {
      onSuccess: () => {
        // Invalider les caches
        queryClient.invalidateQueries(['livres']);
        queryClient.invalidateQueries(['livre', id]);
        queryClient.invalidateQueries('tags-list');
        queryClient.invalidateQueries('auteurs-list');
        queryClient.invalidateQueries('editeurs-list');
        // Rediriger vers la liste des livres
        navigate('/livres');
      },
      onError: (error) => {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  );

  // Mutation pour mettre à jour le statut de lecture
  const updateStatusMutation = useMutation(
    (newStatus) => livresAPI.update(id, { statut_lecture: newStatus }),
    {
      onSuccess: () => {
        toast.success(t('livreDetailPage.statusUpdateSuccess'));
        // Invalider les caches pour mettre à jour l'affichage
        queryClient.invalidateQueries(['livre', id]);
        queryClient.invalidateQueries(['livres']);
      },
      onError: (error) => {
        console.error('Erreur lors de la mise à jour du statut:', error);
        toast.error(t('livreDetailPage.statusUpdateError'));
      },
    }
  );

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus !== livre?.statut_lecture) {
      updateStatusMutation.mutate(newStatus);
    }
  };

  const handleDeleteConfirm = () => {
    deleteLivreMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleImageClick = (src, alt) => {
    setImageModal({ open: true, src, alt });
  };

  const handleImageModalClose = () => {
    setImageModal({ open: false, src: '', alt: '' });
  };


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('livreDetailPage.loadingError', { message: error.message })}
      </Alert>
    );
  }

  if (!livre) {
    return (
      <Alert severity="info">
        {t('livreDetailPage.notFound')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header avec navigation */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
            >
              {t('livreDetailPage.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/livres/${id}/modifier`)}
                size="small"
                sx={{ 
                  height: '52px',
                  borderRadius: 2,
                  fontSize: '16px',
                }}
              >
                {t('livreDetailPage.edit')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                size="small"
                sx={{ 
                  height: '52px',
                  borderRadius: 2,
                  fontSize: '16px',
                }}
              >
                {t('livreDetailPage.delete')}
              </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Colonne gauche - Image */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '3/4',
              width: '100%',
              position: 'relative'
            }}
          >
            <CardMedia
              component="img"
              image={livre.photo_couverture 
                ? `${UPLOADS_URL}/covers/${livre.photo_couverture}` 
                : '/blank-book.png'}
              alt={`Couverture de ${livre.titre}`}
              sx={{ 
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                cursor: livre.photo_couverture ? 'pointer' : 'default'
              }}
              onClick={livre.photo_couverture 
                ? () => handleImageClick(`${UPLOADS_URL}/covers/${livre.photo_couverture}`, `Couverture de ${livre.titre}`)
                : undefined}
            />
          </Box>
        </Grid>

        {/* Colonne droite - Informations */}
        <Grid item xs={12} md={8}>
          <Box>
            {/* Titre */}
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                fontSize: '24px',
                mb: 2,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {livre.titre}
            </Typography>

            {/* Statut de lecture */}
            <Box sx={{ mb: 3 }}>
              <StatusChip
                statut={livre.statut_lecture}
                onStatusChange={handleStatusChange}
                isLoading={updateStatusMutation.isLoading}
                editable={true}
              />
            </Box>

            {/* Auteur */}
            {livre.auteurs && livre.auteurs.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {/* Première ligne : icône + nom du champ */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <BookIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'text.secondary',
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.author')}
                  </Typography>
                </Box>
                {/* Deuxième ligne : valeur alignée avec l'icône */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 0 }}>
                  {livre.auteurs.map((auteur, index) => (
                    <React.Fragment key={auteur.id || index}>
                      {index > 0 && (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '16px',
                            fontWeight: 400,
                            color: 'text.primary',
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          }}
                        >
                          ,{' '}
                        </Typography>
                      )}
                      <Link
                        component="button"
                        variant="body1"
                        onClick={() => navigate(`/auteurs/${auteur.id}`)}
                        sx={{
                          fontSize: '16px',
                          fontWeight: 400,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          color: 'text.primary',
                          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {`${auteur.prenom || ''} ${auteur.nom}`.trim()}
                      </Link>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}

            {/* Année */}
            {livre.annee_publication && (
              <Box sx={{ mb: 2 }}>
                {/* Première ligne : icône + nom du champ */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'text.secondary',
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.year')}
                  </Typography>
                </Box>
                {/* Deuxième ligne : valeur alignée avec l'icône */}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'text.primary',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    ml: 0,
                  }}
                >
                  {livre.annee_publication}
                </Typography>
              </Box>
            )}

            {/* Emplacement */}
            {(livre.bibliotheque_nom || livre.rayonnage_nom) && (
              <Box sx={{ mb: 2 }}>
                {/* Première ligne : icône + nom du champ */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <HomeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'text.secondary',
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.location')}
                  </Typography>
                </Box>
                {/* Deuxième ligne : valeur alignée avec l'icône */}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'text.primary',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    ml: 0,
                  }}
                >
                  {livre.bibliotheque_nom || t('livreDetailPage.noLibrary')}
                  {livre.rayonnage_nom && ` > ${livre.rayonnage_nom}`}
                </Typography>
              </Box>
            )}

            {/* Éditeur */}
            {livre.editeur_id && (livre.editeur_nom || (livre.editeur && livre.editeur.nom)) ? (
              <Box sx={{ mb: 2 }}>
                {/* Première ligne : icône + nom du champ */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <BusinessIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'text.secondary',
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.publisher')}
                  </Typography>
                </Box>
                {/* Deuxième ligne : valeur alignée avec l'icône */}
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => navigate(`/editeurs/${livre.editeur_id}`)}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    color: 'text.primary',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    ml: 0,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {livre.editeur_nom || (livre.editeur ? livre.editeur.nom : '')}
                </Link>
              </Box>
            ) : (
              <InfoItem
                icon={<BusinessIcon />}
                label={t('livreDetailPage.publisher')}
                value={t('livreDetailPage.notSet')}
                showIfEmpty={true}
              />
            )}

            {/* Édition */}
            <InfoItem
              icon={<DescriptionIcon />}
              label={t('livreDetailPage.edition')}
              value={livre.edition}
            />

            {/* ISBN */}
            <InfoItem
              icon={<QrCodeIcon />}
              label={t('livreDetailPage.isbn')}
              value={livre.isbn}
            />

            {/* Nombre de pages */}
            <InfoItem
              icon={<PagesIcon />}
              label={t('livreDetailPage.pageCount')}
              value={livre.nombre_pages ? `${livre.nombre_pages} ${t('livreDetailPage.pages')}` : null}
            />

            {/* État du livre */}
            <InfoItem
              icon={<StarIcon />}
              label={t('bookCondition.label')}
              value={getEtatLabel(livre.etat_livre, t) || t('bookCondition.notSet')}
              showIfEmpty={true}
            />

            {/* Étiquettes */}
            <Box sx={{ mb: 2 }}>
              {/* Première ligne : icône + nom du champ */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <LabelIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'text.secondary',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.tags')}
                  </Typography>
              </Box>
              {/* Deuxième ligne : valeur alignée avec l'icône */}
              <Box sx={{ ml: 0 }}>
                <TagList 
                  tags={getLivreTagsWithColors(livre)}
                  size="small"
                  editable={true}
                  onTagClick={(tag) => navigate(`/tags/${tag.id}`)}
                  onTagRemove={(tag) => removeTagMutation.mutate({ livreId: livre.id, tagId: tag.id })}
                  onTagAdd={(tagId) => addTagMutation.mutate({ livreId: livre.id, tagId })}
                  availableTags={tagsData || []}
                  isLoading={isTagLoading}
                />
              </Box>
            </Box>

            {/* Résumé */}
            {livre.resume && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SummarizeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                      fontSize: '18px',
                      fontWeight: 600,
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.summary')}
                  </Typography>
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    ml: 0,
                  }}
                >
                  {livre.resume}
                </Typography>
              </Box>
            )}

            {/* Notes personnelles */}
            {livre.notes_personnelles && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NoteIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                  <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                      fontSize: '18px',
                      fontWeight: 600,
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.personalNotes')}
                  </Typography>
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '16px',
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    ml: 0,
                  }}
                >
                  {livre.notes_personnelles}
                </Typography>
              </Box>
            )}

            {/* Métadonnées */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
                <Typography 
                  variant="h6" 
                  component="h3"
                  sx={{ 
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'oklch(0.551 0.027 264.364)',
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                  {t('livreDetailPage.metadata')}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 0, sm: 2 },
                ml: 0 
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'oklch(0.446 0.03 256.802)',
                    mb: 0.5,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {t('livreDetailPage.createdAt')} {livre.created_at ? format(new Date(livre.created_at), 'dd MMMM yyyy', { locale: dateLocale }) : 'Invalid Date'}
                </Typography>
                {livre.updated_at && livre.updated_at !== livre.created_at && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'oklch(0.446 0.03 256.802)',
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('livreDetailPage.updatedAt')} {format(new Date(livre.updated_at), 'dd MMMM yyyy', { locale: dateLocale })}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('livreDetailPage.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('livreDetailPage.deleteDialog.message', { title: livre?.titre })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('livreDetailPage.deleteDialog.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLivreMutation.isLoading}
          >
            {deleteLivreMutation.isLoading ? t('livreDetailPage.deleteDialog.deleting') : t('livreDetailPage.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'affichage d'image */}
      <Dialog
        open={imageModal.open}
        onClose={handleImageModalClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <Box
            component="img"
            src={imageModal.src}
            alt={imageModal.alt}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block'
            }}
          />
          <IconButton
            onClick={handleImageModalClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            ✕
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default LivreDetailPage;
